import { Box, Chip, Alert } from '@nimbus-ds/components';
import { PaymentOptionInfoDto } from '@tiendanube/common';
import { SelectOrEditLink } from 'commons/components';
import { useModal } from 'commons/hooks';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { PaymentOptionsModal } from './PaymentOptionsModal';
import { buildPaymentOptionDescription, getPaymentOptionIcon } from './utils';

interface PaymentOptionSelectorProps {
  paymentOptions: PaymentOptionInfoDto[];
  onChange: (paymentOptions: PaymentOptionInfoDto[]) => void;
  requiredText: string;
  shouldLimitToNuvemPago: boolean;
}

export function PaymentOptionSelector({
  paymentOptions,
  onChange,
  requiredText,
  shouldLimitToNuvemPago,
}: Readonly<PaymentOptionSelectorProps>): JSX.Element {
  const t = useTranslationMarketing(
    'promotions.settingsPage.limits.paymentOptions',
  );

  const [isModalOpen, openModal, closeModal] = useModal();

  const hasSelectedPaymentOptions = paymentOptions.length > 0;

  const handleOnChange = (paymentOptions: PaymentOptionInfoDto[]) => {
    onChange(paymentOptions);
  };

  return (
    <>
      {hasSelectedPaymentOptions && (
        <Box display="flex" gap="4" flexWrap="wrap">
          {paymentOptions.map((paymentOption) => {
            const text = buildPaymentOptionDescription(
              paymentOption.translationKey,
              paymentOption.description,
              paymentOption.paymentProviderName,
              t,
            );
            const IconComponent = getPaymentOptionIcon(
              paymentOption.translationKey,
            );

            return (
              <Chip
                key={paymentOption.id}
                icon={<IconComponent />}
                text={text}
                removable
                onClick={() =>
                  onChange(
                    paymentOptions.filter(
                      (current) => current.id !== paymentOption.id,
                    ),
                  )
                }
              />
            );
          })}
        </Box>
      )}

      <>
        <SelectOrEditLink
          isEdit={hasSelectedPaymentOptions}
          onSelect={openModal}
          selectLinkText={t('selector.select')}
          editLinkText={t('selector.edit')}
        />

        {!!requiredText && <Alert appearance="danger">{requiredText}</Alert>}

        <PaymentOptionsModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onChange={handleOnChange}
          paymentOptions={paymentOptions}
          shouldLimitToNuvemPago={shouldLimitToNuvemPago}
        />
      </>
    </>
  );
}
